import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Send, WifiOff, RefreshCw, AlertCircle, Lock, MessageCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { leadQueriesApi, ticketApi } from '@/lib/api';
import { adminSocket } from '@/lib/admin-socket';
import { MESSAGE_PAGE_LIMIT, fetchLatestMessageSlice, mergeLeadMessages } from '@/lib/lead-query-messages';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { LeadQuery, LeadQueryMessage, LeadQueryWebSocketMessage } from '@/types/lead-query';
import { participantsFromPresenceApiResponse } from '@/lib/chat-presence';
import type { ChatPresenceParticipant } from '@/types/chat-presence';
import { LEAD_TRANSFER_TO_REPRESENTATIVE_MESSAGE } from '@/lib/lead-query-copy';
import { useChatPresence } from '@/hooks/useChatPresence';
import { VisitorPresenceBar } from '@/components/chat/VisitorPresenceBar';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';

const MAX_MESSAGE_LENGTH = 1000;

function readyStorageKey(id: string) {
  return `lead-query:${id}:ready`;
}

interface QueryDetailProps {
  queryId: string;
  /** Set after accepting an incoming handoff so the composer unlocks immediately. */
  initialReadyToChat?: boolean;
  onInitialReadyConsumed?: () => void;
  /** Registers inbox handoff modal listeners once, after the first `connect()` from Ready to chat. */
  onAttachLeadHandoffListeners?: () => void;
}

export const QueryDetail: React.FC<QueryDetailProps> = ({
  queryId,
  initialReadyToChat = false,
  onInitialReadyConsumed,
  onAttachLeadHandoffListeners,
}) => {
  const { user } = useAuth();
  const [query, setQuery] = useState<LeadQuery | null>(null);
  const [messages, setMessages] = useState<LeadQueryMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<
    'connected' | 'connecting' | 'disconnected' | 'reconnecting'
  >('disconnected');
  const [availableAdmins, setAvailableAdmins] = useState<{ adminId: string; adminName: string }[]>([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  const [oldestLoadedPage, setOldestLoadedPage] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const markedLeadMessageIdsRef = useRef<Set<string>>(new Set());
  const readyToChatRef = useRef(false);
  /** After a false→true Ready transition, pull latest page from API so we don't miss messages sent while socket was off. */
  const dbSyncedAfterReadyRef = useRef(false);
  const [presenceRefreshSignal, setPresenceRefreshSignal] = useState(0);

  const [readyToChat, setReadyToChat] = useState(() => {
    if (initialReadyToChat) return true;
    try {
      return sessionStorage.getItem(readyStorageKey(queryId)) === '1';
    } catch {
      return false;
    }
  });
  readyToChatRef.current = readyToChat;

  useEffect(() => {
    if (!initialReadyToChat) return;
    try {
      sessionStorage.setItem(readyStorageKey(queryId), '1');
    } catch {
      /* ignore */
    }
    onInitialReadyConsumed?.();
  }, [initialReadyToChat, queryId, onInitialReadyConsumed]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  };

  const markLeadMessageReadOnce = useCallback((messageId: string) => {
    if (!messageId || markedLeadMessageIdsRef.current.has(messageId)) return;
    markedLeadMessageIdsRef.current.add(messageId);
    adminSocket.markLeadQueryMessageRead(messageId);
  }, []);

  useEffect(() => {
    markedLeadMessageIdsRef.current = new Set();
    dbSyncedAfterReadyRef.current = false;
  }, [queryId]);

  /** Back to inbox / leaving the thread: drop persisted Ready so the next visit starts with socket off. */
  useEffect(() => {
    const id = queryId;
    return () => {
      try {
        sessionStorage.removeItem(readyStorageKey(id));
      } catch {
        /* ignore */
      }
    };
  }, [queryId]);

  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    for (const m of messages) {
      if (m.senderType === 'lead' && m.messageId && !m.isRead) {
        markLeadMessageReadOnce(m.messageId);
      }
    }
  }, [messages, connectionStatus, markLeadMessageReadOnce]);

  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const refreshQuery = useCallback(async () => {
    const res = await leadQueriesApi.getQuery(queryId);
    if (res.statusCode === 200 && res.data?.query) {
      setQuery(res.data.query as LeadQuery);
    }
  }, [queryId]);

  const fetchLeadPresence = useCallback(async (): Promise<ChatPresenceParticipant[] | null> => {
    const res = await leadQueriesApi.getPresence(queryId);
    return participantsFromPresenceApiResponse(res);
  }, [queryId]);

  const { participants: presenceParticipants } = useChatPresence({
    enabled: Boolean(queryId && user && query && !error),
    fetchParticipants: fetchLeadPresence,
    pollMs: 20_000,
    refreshSignal: presenceRefreshSignal,
  });

  const loadAvailableAdmins = async () => {
    try {
      const response = await ticketApi.getAdmins();
      if (((response as { statusCode?: number; success?: boolean }).statusCode === 200 || response.success) && response.data) {
        const admins = (response.data.admins as { adminId?: string; _id?: string; adminName?: string; fullName?: string }[]).map(
          (a) => ({
            adminId: a.adminId || a._id || '',
            adminName: a.adminName || a.fullName || 'Admin',
          })
        );
        setAvailableAdmins(admins.filter((a) => a.adminId));
      }
    } catch {
      setAvailableAdmins([]);
    }
  };

  /** REST only — Socket.IO connects only when Ready to chat is ON. */
  useEffect(() => {
    if (!queryId || !user) return;

    const load = async () => {
      try {
        setError(null);
        setLoading(true);
        setConnectionStatus('disconnected');

        void loadAvailableAdmins();

        const qRes = await leadQueriesApi.getQuery(queryId);
        if (qRes.statusCode !== 200 || !qRes.data?.query) {
          throw new Error((qRes as { message?: string }).message || 'Query not found');
        }
        const q = qRes.data.query as LeadQuery;
        setQuery(q);

        const slice = await fetchLatestMessageSlice(queryId, MESSAGE_PAGE_LIMIT);
        setMessages(slice.messages);
        setOldestLoadedPage(slice.oldestLoadedPage);
        setTotalMessages(slice.total);
        setTimeout(() => scrollToBottom(), 200);
      } catch (e) {
        console.error(e);
        setError(e instanceof Error ? e.message : 'Failed to load query');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [queryId, user]);

  /** Live socket + room join only while Ready to chat is ON (first connect happens here). */
  useEffect(() => {
    if (!queryId || !user) return;

    if (!readyToChat) {
      setConnectionStatus('disconnected');
      adminSocket.leaveLeadQuery(queryId);
      adminSocket.setAvailability('offline');
      return;
    }

    const onNewMessage = (data: LeadQueryWebSocketMessage) => {
      if (String(data.queryId ?? '').trim() !== String(queryId).trim()) return;
      setMessages((prev) => {
        if (prev.some((m) => m.messageId === data.messageId)) return prev;
        const row: LeadQueryMessage = {
          _id: data.messageId,
          messageId: data.messageId,
          queryId: data.queryId,
          senderId: data.senderId,
          senderType: data.senderType,
          senderName: data.senderName || 'Unknown',
          message: data.message,
          createdAt: data.createdAt,
          messageType: data.messageType,
          deliveryStatus: data.deliveryStatus,
        };
        return [...prev, row];
      });
      if (data.senderType === 'lead' && data.messageId) {
        markLeadMessageReadOnce(data.messageId);
      }
      setTimeout(() => scrollToBottom(), 50);
    };

    const onDisconnect = () => setConnectionStatus('disconnected');

    const onSocketConnect = () => {
      setConnectionStatus('connected');
      adminSocket.joinLeadQuery(queryId);
      adminSocket.setAvailability('available');
      setPresenceRefreshSignal((n) => n + 1);
    };

    const onReconnect = () => {
      setConnectionStatus('reconnecting');
      adminSocket.joinLeadQuery(queryId);
      adminSocket.setAvailability('available');
      setPresenceRefreshSignal((n) => n + 1);
      setTimeout(() => setConnectionStatus('connected'), 400);
    };

    let cancelled = false;

    const run = async () => {
      try {
        setConnectionStatus('connecting');
        await adminSocket.connect();
        onAttachLeadHandoffListeners?.();
        if (cancelled) return;

        adminSocket.onSocketConnect(onSocketConnect);
        adminSocket.onLeadQueryNewMessage(onNewMessage);
        adminSocket.onDisconnect(onDisconnect);
        adminSocket.onReconnect(onReconnect);

        if (adminSocket.connected) {
          setConnectionStatus('connected');
          adminSocket.joinLeadQuery(queryId);
          adminSocket.setAvailability('available');
          setPresenceRefreshSignal((n) => n + 1);
        }
      } catch (wsErr) {
        console.warn('Lead query WebSocket failed:', wsErr);
        if (!cancelled) setConnectionStatus('disconnected');
      }
    };

    void run();

    return () => {
      cancelled = true;
      adminSocket.offSocketConnect(onSocketConnect);
      adminSocket.leaveLeadQuery(queryId);
      adminSocket.setAvailability('offline');
      adminSocket.offLeadQueryNewMessage(onNewMessage);
      adminSocket.offDisconnect(onDisconnect);
      adminSocket.offReconnect(onReconnect);
    };
  }, [readyToChat, queryId, user, onAttachLeadHandoffListeners, markLeadMessageReadOnce]);

  /**
   * When Ready turns on, re-fetch the latest message page from the DB before relying on the socket alone.
   * Socket events are not replayed for history; this closes the gap between the last 10s poll and `join-lead-query`.
   */
  useEffect(() => {
    if (!queryId || !user || loading || error) return;
    if (!readyToChat) {
      dbSyncedAfterReadyRef.current = false;
      return;
    }
    if (dbSyncedAfterReadyRef.current) return;
    dbSyncedAfterReadyRef.current = true;

    let cancelled = false;
    void (async () => {
      try {
        const slice = await fetchLatestMessageSlice(queryId, MESSAGE_PAGE_LIMIT);
        if (cancelled) return;
        setMessages((prev) => mergeLeadMessages(prev, slice.messages));
        setTotalMessages(slice.total);
        setOldestLoadedPage(slice.oldestLoadedPage);
        setTimeout(() => scrollToBottom(), 100);
      } catch {
        /* ignore — polling will retry */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readyToChat, queryId, user, loading, error]);

  // Polling fallback: lead messages only appear when the socket delivers `new-lead-query-message`.
  // If the server omits that for admins, or the room/join fails silently, REST still has the truth.
  useEffect(() => {
    if (!queryId || !user || loading || error) return;

    const POLL_MS = 10_000;

    const tick = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const slice = await fetchLatestMessageSlice(queryId, MESSAGE_PAGE_LIMIT);
        setMessages((prev) => {
          const merged = mergeLeadMessages(prev, slice.messages);
          if (
            merged.length === prev.length &&
            merged.every((m, i) => m.messageId === prev[i]?.messageId)
          ) {
            return prev;
          }
          return merged;
        });
        setTotalMessages(slice.total);
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      } catch {
        /* ignore transient poll errors */
      }
    };

    const id = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(id);
  }, [queryId, user, loading, error]);

  const hasMoreOlder = oldestLoadedPage > 1;

  const loadOlder = async () => {
    if (loadingOlder || !hasMoreOlder) return;
    const el = scrollContainerRef.current;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    const prevScrollTop = el?.scrollTop ?? 0;
    setLoadingOlder(true);
    try {
      const page = oldestLoadedPage - 1;
      const res = await leadQueriesApi.getMessages(queryId, { page, limit: MESSAGE_PAGE_LIMIT });
      if (res.statusCode !== 200) {
        throw new Error((res as { message?: string }).message || 'Failed to load older messages');
      }
      const incoming = res.data.messages as LeadQueryMessage[];
      setMessages((prev) => {
        const merged = [...incoming, ...prev];
        const seen = new Set<string>();
        return merged.filter((m) => {
          if (seen.has(m.messageId)) return false;
          seen.add(m.messageId);
          return true;
        });
      });
      setOldestLoadedPage(page);
      requestAnimationFrame(() => {
        if (el) {
          el.scrollTop = prevScrollTop + (el.scrollHeight - prevScrollHeight);
        }
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  const handleReadyChange = (checked: boolean) => {
    setReadyToChat(checked);
    try {
      if (checked) sessionStorage.setItem(readyStorageKey(queryId), '1');
      else sessionStorage.removeItem(readyStorageKey(queryId));
    } catch {
      /* ignore */
    }
  };

  const mergeMessageFromApi = (raw: Record<string, unknown>, fallbackText: string): LeadQueryMessage => ({
    _id: String(raw._id ?? raw.messageId ?? ''),
    messageId: String(raw.messageId ?? ''),
    queryId: String(raw.queryId ?? queryId),
    leadQueryId: raw.leadQueryId != null ? String(raw.leadQueryId) : undefined,
    senderId: String(raw.senderId ?? ''),
    senderType: (raw.senderType === 'lead' ? 'lead' : raw.senderType === 'bot' ? 'bot' : 'admin') as LeadQueryMessage['senderType'],
    senderName: String(raw.senderName ?? user?.fullName ?? 'You'),
    message: String(raw.message ?? fallbackText),
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    messageType: raw.messageType != null ? String(raw.messageType) : undefined,
    deliveryStatus: raw.deliveryStatus != null ? String(raw.deliveryStatus) : undefined,
    isRead: raw.isRead === true,
  });

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    if (!readyToChat) {
      setMessageError('Turn on Ready to chat to send messages.');
      return;
    }
    if (newMessage.length > MAX_MESSAGE_LENGTH) {
      setMessageError(`Message too long. Maximum ${MAX_MESSAGE_LENGTH} characters.`);
      return;
    }
    if (!isOnline) {
      setMessageError('Cannot send while offline.');
      return;
    }
    if (query?.status === 'closed') {
      setMessageError('This query is closed.');
      return;
    }

    const trimmed = newMessage.trim();

    try {
      setMessageError(null);
      setSending(true);

      // Always POST so we get `data.message` and can append immediately. Socket may not echo to sender
      // reliably; duplicate `new-lead-query-message` is deduped by messageId.
      const res = await leadQueriesApi.sendMessage(queryId, trimmed);
      setNewMessage('');

      const resAny = res as {
        statusCode?: number;
        success?: boolean;
        data?: { message?: Record<string, unknown> };
      };
      const ok =
        resAny.statusCode === 200 ||
        resAny.statusCode === 201 ||
        resAny.success === true;
      const payload = resAny.data?.message;
      if (ok && payload && typeof payload === 'object') {
        const row = mergeMessageFromApi(payload, trimmed);
        if (row.messageId) {
          setMessages((prev) => {
            if (prev.some((m) => m.messageId === row.messageId)) return prev;
            return [...prev, row];
          });
          setTotalMessages((t) => t + 1);
        }
      }

      setTimeout(() => scrollToBottom(), 80);
      await refreshQuery();
    } catch (e) {
      setMessageError(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedAdminId) return;
    setAssigning(true);
    try {
      const res = await leadQueriesApi.assign(queryId, { adminId: selectedAdminId });
      if (res.statusCode === 200 && res.data?.query) {
        setQuery(res.data.query as LeadQuery);
        toast.success('Query assigned');
        setSelectedAdminId('');
      } else {
        throw new Error((res as { message?: string }).message || 'Assign failed');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Assign failed');
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignToMe = async () => {
    setAssigning(true);
    try {
      const res = await leadQueriesApi.assign(queryId, {});
      if (res.statusCode === 200 && res.data?.query) {
        setQuery(res.data.query as LeadQuery);
        toast.success('Assigned to you');
      } else {
        throw new Error((res as { message?: string }).message || 'Assign failed');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Assign failed');
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      const res = await leadQueriesApi.close(queryId);
      if (res.statusCode === 200 && res.data?.query) {
        setQuery(res.data.query as LeadQuery);
        toast.success('Query closed');
        setCloseDialogOpen(false);
      } else {
        throw new Error((res as { message?: string }).message || 'Close failed');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Close failed');
    } finally {
      setClosing(false);
    }
  };

  const formatTime = (iso: string) => formatAdminBusinessEt(iso);

  if (error) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Failed to load query</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => window.location.reload()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </CardContent>
          </Card>
        </div>
        <div className="lg:col-span-2">
          <Card className="h-[560px]">
            <CardContent className="p-4 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!query) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">Query not found.</p>
      </Card>
    );
  }

  const isClosed = query.status === 'closed';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {!isOnline && (
        <div className="lg:col-span-3 bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded text-sm flex items-center gap-2">
          <WifiOff className="h-4 w-4 shrink-0" />
          You&apos;re offline. Sending may fail until you reconnect.
        </div>
      )}

      {!readyToChat && isOnline && (
        <div className="lg:col-span-3 bg-muted/50 border border-border text-foreground px-4 py-3 rounded text-sm">
          Turn on <span className="font-medium">Ready to chat</span> below to connect the socket, receive handoff
          alerts, and get live messages. Until then this view still loads history and refreshes about every 10 seconds.
        </div>
      )}

      {readyToChat && connectionStatus === 'disconnected' && isOnline && (
        <div className="lg:col-span-3 bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded text-sm">
          Could not connect to the live channel — check API URL and network. Sending still works via REST; messages
          refresh periodically.
        </div>
      )}

      <div className="lg:col-span-1 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Lead</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Query ID</p>
              <p className="font-mono text-xs">{query.queryId}</p>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">Status</span>
              <Badge variant="outline" className="capitalize">
                {query.status}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Name</p>
              <p className="font-medium">{query.fullName?.trim() || "Website visitor"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="break-all">{query.email?.trim() || "Email not shared yet"}</p>
            </div>
            {query.phone && (
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p>{query.phone}</p>
              </div>
            )}
            {query.assignedTo && (
              <div className="flex gap-2 items-start">
                <div>
                  <p className="text-xs text-muted-foreground">Assigned</p>
                  <p className="font-medium">{query.assignedTo.adminName}</p>
                  <p className="text-xs text-muted-foreground">{query.assignedTo.adminEmail}</p>
                </div>
              </div>
            )}
            {query.lastMessageAt && (
              <div>
                <p className="text-xs text-muted-foreground">Last message</p>
                <p>{formatTime(query.lastMessageAt)}</p>
              </div>
            )}
            {query.mode && (
              <div>
                <p className="text-xs text-muted-foreground">Mode</p>
                <Badge variant="secondary" className="capitalize">
                  {query.mode.replace(/_/g, ' ')}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Assignment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Any admin can reply; assignment is for reporting.
            </p>
            <div className="flex flex-col gap-2">
              <Select value={selectedAdminId} onValueChange={setSelectedAdminId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select admin" />
                </SelectTrigger>
                <SelectContent>
                  {availableAdmins.map((a) => (
                    <SelectItem key={a.adminId} value={a.adminId}>
                      {a.adminName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAssign} disabled={!selectedAdminId || assigning || isClosed}>
                  Assign
                </Button>
                <Button size="sm" variant="secondary" onClick={handleAssignToMe} disabled={assigning || isClosed}>
                  Assign to me
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Close</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              size="sm"
              className="w-full"
              disabled={isClosed || closing}
              onClick={() => setCloseDialogOpen(true)}
            >
              <Lock className="h-4 w-4 mr-2" />
              {isClosed ? 'Closed' : 'Close query'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-2 flex flex-col gap-3 min-h-[560px]">
        <Card className="flex-1 flex flex-col min-h-0">
          <CardHeader className="py-3 border-b space-y-3">
            <VisitorPresenceBar participants={presenceParticipants} />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Conversation</CardTitle>
                <span className="text-xs text-muted-foreground">
                  {totalMessages} message{totalMessages === 1 ? '' : 's'}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <MessageCircle className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Ready to chat</span>
                <span
                  className={
                    readyToChat ? 'text-xs font-medium text-green-700' : 'text-xs font-medium text-muted-foreground'
                  }
                >
                  {readyToChat ? 'On' : 'Off'}
                </span>
                <Switch
                  checked={readyToChat}
                  onCheckedChange={handleReadyChange}
                  disabled={isClosed}
                  aria-label="Ready to chat in this conversation"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              The website may show “{LEAD_TRANSFER_TO_REPRESENTATIVE_MESSAGE}” before you join. Turning this on
              advertises you in the handoff pool and unlocks the message box for this thread only.
            </p>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col p-0 min-h-0">
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto px-4 py-3 space-y-3 max-h-[420px] md:max-h-[min(52vh,480px)]"
            >
              {hasMoreOlder && (
                <div className="flex justify-center pb-2">
                  <Button variant="outline" size="sm" onClick={loadOlder} disabled={loadingOlder}>
                    {loadingOlder ? 'Loading…' : 'Load older messages'}
                  </Button>
                </div>
              )}
              {messages.map((m) => {
                const isAdmin = m.senderType === 'admin';
                const isBot = m.senderType === 'bot';
                return (
                  <div
                    key={m.messageId}
                    className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                        isAdmin
                          ? 'bg-primary text-primary-foreground'
                          : isBot
                            ? 'bg-indigo-50 text-indigo-900 border border-indigo-200'
                            : 'bg-muted'
                      }`}
                    >
                      <p className="text-xs opacity-80 mb-1">
                        {m.senderName} · {formatTime(m.createdAt)}
                      </p>
                      <p className="whitespace-pre-wrap break-words">{m.message}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div className="border-t p-3 space-y-2">
              {messageError && <p className="text-xs text-destructive">{messageError}</p>}
              {isClosed ? (
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  This query is closed. The lead cannot send messages; you can still read history.
                </p>
              ) : (
                <>
                  {!readyToChat && (
                    <p className="text-xs text-muted-foreground">Turn on Ready to chat above to type and send.</p>
                  )}
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={readyToChat ? 'Type a message…' : 'Enable Ready to chat to reply…'}
                    className="min-h-[88px] resize-none"
                    maxLength={MAX_MESSAGE_LENGTH}
                    disabled={sending || !readyToChat}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      {newMessage.length}/{MAX_MESSAGE_LENGTH}
                    </span>
                    <Button
                      onClick={() => void handleSend()}
                      disabled={sending || !newMessage.trim() || !readyToChat}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this query?</AlertDialogTitle>
            <AlertDialogDescription>
              The visitor will no longer be able to send messages on this thread. You can still view history here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleClose()} disabled={closing}>
              {closing ? 'Closing…' : 'Close query'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
